import CloudUploadIcon from '@material-design-icons/svg/filled/cloud_upload.svg?react';
import InfoIcon from '@material-design-icons/svg/filled/info.svg?react';
import type { ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useArweaveUpload } from '../hooks/useArweaveUpload';
import { useMintFlow } from '../hooks/useMintFlow';
import { useTRPC } from '../hooks/useTRPC';
import { Button } from './Button';
import {
  NftMintConnectedWalletBlock,
  NftMintDisconnectedWalletPrompt,
  NftMintErrorBlock,
  NftMintModalFrame,
  NftMintRequiredWalletBlock,
  NftMintSuccessBlock,
  NftMintWrongWalletMessage,
} from './NftMintModalPresentation';

import { getErrorMessage } from '../utils/errorHandling';
import { addBreadcrumb } from '../utils/errorReporting';
import {
  mintReleaseNftWorkflow,
  resumeReleaseNftWorkflow,
  type ReleaseMintWorkflowResult,
} from '../utils/nftMinting/mintNftWorkflow';
import {
  calculateAdaptiveDownloadTimeoutMs,
  downloadFileWithPresignedUrl,
  fetchAssetWithPresignedUrl,
  fetchFirstAvailableAssetWithPresignedUrl,
  getReusableR2ReleaseApk,
} from '../utils/nftMintUtils';
import { buildReleaseNftName } from '../utils/releaseNftName';
import { SOLANA_NETWORK_LABEL } from '../utils/solanaUtils';

interface ReleaseNftMintModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: (mintAddress: string, txSignature: string) => void;
  releaseData: {
    id: string;
    releaseName: string;
    releaseVersion: string;
    androidPackage: string;
    versionCode: number;
    permissions: string[];
    minSdk: number;
    targetSdk: number | null;
    certificateFingerprint: string;
    releaseFileUrl: string;
    releaseFileName: string;
    releaseFileSize: number;
    releaseFileHash: string;
    publisherAddress: string;
    appMintAddress: string;
    shortDescription?: string;
    longDescription?: string;
    newInVersion?: string;
    localizedName?: string;
    locales?: string[];
  };
  dappData: {
    id: string;
    dappName: string;
    subtitle?: string;
    description?: string;
    dappIconUrl?: string;
    dappIconThumbnailUrl?: string;
    bannerUrl?: string;
    featureGraphicUrl?: string;
    editorsChoiceGraphicUrl?: string;
    dappPreviewUrls?: string[];
    publisherName?: string;
    publisherWebsite?: string;
    publisherEmail?: string;
    contactEmail?: string;
    supportEmail?: string;
    licenseUrl?: string;
    copyrightUrl?: string;
    privacyPolicyUrl?: string;
  };
}

interface UploadResult {
  metadataUri: string;
  uploadProvider?: 'Arweave' | 'S3' | 'R2' | 'IPFS';
  uploadProviderId?: string;
}

const trimToUndefined = (value?: string | null) => {
  const trimmed = value?.trim();
  return trimmed ? trimmed : undefined;
};

const getResolvedReleaseMetadata = (
  releaseData: ReleaseNftMintModalProps['releaseData'],
  dappData: ReleaseNftMintModalProps['dappData'],
) => {
  const dappDescription = trimToUndefined(dappData.description);
  const shortDescription =
    trimToUndefined(releaseData.shortDescription) ||
    trimToUndefined(dappData.subtitle) ||
    dappDescription?.slice(0, 50);
  const longDescription =
    trimToUndefined(releaseData.longDescription) || dappDescription;
  const localizedName =
    trimToUndefined(releaseData.localizedName) ||
    trimToUndefined(dappData.dappName);
  const newInVersion =
    trimToUndefined(releaseData.newInVersion) || 'Initial release';

  return {
    shortDescription,
    longDescription,
    localizedName,
    newInVersion,
    locales:
      releaseData.locales && releaseData.locales.length > 0
        ? releaseData.locales
        : ['en-US'],
  };
};

type UploadStep =
  | 'preparing'
  | 'apk'
  | 'icon'
  | 'screenshots'
  | 'metadata'
  | 'complete';

const UPLOAD_STEP_MESSAGES: Record<UploadStep, string> = {
  preparing: 'Preparing upload...',
  apk: 'Uploading APK...',
  icon: 'Uploading app icon...',
  screenshots: 'Uploading screenshots...',
  metadata: 'Uploading NFT metadata...',
  complete: 'Upload complete!',
};

export function ReleaseNftMintModal({
  isOpen,
  onClose,
  onSuccess,
  releaseData,
  dappData,
}: ReleaseNftMintModalProps) {
  const {
    wallet,
    connected,
    publicKey,
    isLoading,
    setIsLoading,
    mintResult,
    lastError,
    retryCount,
    isCorrectWallet,
    validateWallet,
    handleMintError,
    handleMintSuccess,
  } = useMintFlow({
    isOpen,
    requiredWalletAddress: releaseData.publisherAddress,
  });
  const [uploadStep, setUploadStep] = useState<UploadStep>('preparing');
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadStatusMessage, setUploadStatusMessage] = useState<string | null>(
    null,
  );
  const [isWaitingForSignature, setIsWaitingForSignature] = useState(false);
  const trpc = useTRPC();
  const { uploadProvider, uploadReleaseNftData } = useArweaveUpload();

  useEffect(() => {
    if (isOpen) {
      setUploadStep('preparing');
      setUploadProgress(0);
      setUploadStatusMessage(null);
      setIsWaitingForSignature(false);
    }
  }, [isOpen]);

  const getUploadStepDisplay = () => {
    if (isWaitingForSignature) {
      return 'Waiting for wallet signature...';
    }

    if (uploadStatusMessage) {
      return uploadStatusMessage;
    }

    return UPLOAD_STEP_MESSAGES[uploadStep] ?? 'Uploading...';
  };

  const handleMint = async () => {
    if (
      !validateWallet({
        missingWalletMessage:
          'No app wallet address found. Please update your app configuration.',
        disconnectedWalletMessage: 'Please connect your wallet first',
        wrongWalletMessage: (walletAddress) =>
          `Please connect the app wallet: ${walletAddress}`,
      })
    ) {
      return;
    }

    if (!publicKey) {
      return;
    }

    setIsLoading(true);
    setUploadProgress(0);
    setUploadStatusMessage(null);
    setIsWaitingForSignature(false);

    let currentUploadStep: UploadStep = 'preparing';
    let apkDownloadTimeoutMs: number | null = null;
    const setCurrentUploadStep = (step: UploadStep) => {
      currentUploadStep = step;
      setUploadStep(step);
    };
    const releaseNftName = buildReleaseNftName({
      localizedName: releaseData.localizedName,
      dappName: dappData.dappName,
      versionName: releaseData.releaseVersion,
      fallbackName: releaseData.releaseName,
    });
    setCurrentUploadStep('preparing');
    const release = {
      releaseId: releaseData.id,
      releaseName: releaseNftName,
      releaseVersion: releaseData.releaseVersion,
      androidPackage: releaseData.androidPackage,
      appMintAddress: releaseData.appMintAddress,
      publisherAddress: releaseData.publisherAddress,
    };
    const completeMint = (result: ReleaseMintWorkflowResult) =>
      handleMintSuccess({
        mintAddress: result.mintAddress,
        txSignature: result.transactionSignature,
        successMessage: 'Release NFT minted successfully!',
        onSuccess,
        onClose,
      });

    try {
      const resumed = await resumeReleaseNftWorkflow({ wallet, trpc, release });
      if (resumed) {
        completeMint(resumed);
        return;
      }
      const balanceResult = await trpc.release.getWalletBalance.query({
        walletAddress: publicKey.toBase58(),
      });

      if (balanceResult._tag === 'Left') {
        throw new Error(
          `Failed to check wallet balance: ${balanceResult.left.message}`,
        );
      }

      const balance = balanceResult.right.balance;

      if (balance < 0.1) {
        throw new Error(
          'Insufficient SOL balance. You need at least 0.1 SOL to mint the Release NFT and cover the required on-chain transaction costs.',
        );
      }

      setCurrentUploadStep('apk');

      const existingApk = getReusableR2ReleaseApk({
        uploadProvider,
        sourceUrl: releaseData.releaseFileUrl,
        fileSize: releaseData.releaseFileSize,
        fileHash: releaseData.releaseFileHash,
      });
      let apkFile: File | undefined;

      if (existingApk) {
        setUploadProgress(5);
        setUploadStatusMessage('Using existing validated APK...');
        addBreadcrumb('Reusing existing R2 release APK', 'nft-mint', {
          releaseId: releaseData.id,
          releaseFileSize: existingApk.size,
        });
      } else {
        toast.info('Downloading APK file...');
        apkDownloadTimeoutMs = calculateAdaptiveDownloadTimeoutMs(
          releaseData.releaseFileSize,
        );

        apkFile = await downloadFileWithPresignedUrl({
          sourceUrl: releaseData.releaseFileUrl,
          fileName: releaseData.releaseFileName,
          mimeType: 'application/vnd.android.package-archive',
          trpc,
          timeoutMs: apkDownloadTimeoutMs,
          maxAttempts: 3,
          onProgress: (progress) => {
            setUploadProgress(progress);
          },
          onRetry: ({ attempt, maxAttempts, error, delayMs }) => {
            const nextAttempt = attempt + 1;
            setUploadProgress(0);
            setUploadStatusMessage(
              `APK download interrupted. Retrying (${nextAttempt}/${maxAttempts})...`,
            );
            addBreadcrumb('Release APK download retry scheduled', 'nft-mint', {
              releaseId: releaseData.id,
              uploadStep: 'apk',
              attempt,
              maxAttempts,
              delayMs,
              reason: error.message,
            });
          },
        });
      }
      setUploadStatusMessage(null);

      let iconFile: File | undefined;
      const screenshotFiles: File[] = [];
      let bannerFile: File | undefined;
      let featureGraphicFile: File | undefined;

      if (!dappData.dappIconUrl && !dappData.dappIconThumbnailUrl) {
        throw new Error(
          'App icon is missing. Please upload an icon before minting the release NFT.',
        );
      }

      try {
        setCurrentUploadStep('icon');
        iconFile = await fetchFirstAvailableAssetWithPresignedUrl(
          [dappData.dappIconUrl, dappData.dappIconThumbnailUrl],
          'app-icon.png',
          'image/png',
          trpc,
        );
      } catch (error) {
        const message = getErrorMessage(error);
        throw new Error(`Failed to download app icon: ${message}`);
      }

      if (dappData.dappPreviewUrls && dappData.dappPreviewUrls.length > 0) {
        setCurrentUploadStep('screenshots');
        for (let i = 0; i < dappData.dappPreviewUrls.length; i++) {
          try {
            const screenshotFile = await fetchAssetWithPresignedUrl(
              dappData.dappPreviewUrls[i],
              `screenshot-${i + 1}.png`,
              'image/png',
              trpc,
            );
            screenshotFiles.push(screenshotFile);
          } catch (error) {
            throw new Error(
              `Failed to fetch screenshot ${i + 1}: ${getErrorMessage(error)}`,
            );
          }
        }
      }

      if (dappData.bannerUrl) {
        try {
          bannerFile = await fetchAssetWithPresignedUrl(
            dappData.bannerUrl,
            'banner.png',
            'image/png',
            trpc,
          );
        } catch (error) {
          throw new Error(`Failed to fetch banner: ${getErrorMessage(error)}`);
        }
      }

      const featureGraphicUrlToUse =
        dappData.editorsChoiceGraphicUrl || dappData.featureGraphicUrl;

      if (featureGraphicUrlToUse) {
        try {
          featureGraphicFile = await fetchAssetWithPresignedUrl(
            featureGraphicUrlToUse,
            'feature-graphic.png',
            'image/png',
            trpc,
          );
        } catch (error) {
          throw new Error(
            `Failed to fetch feature graphic: ${getErrorMessage(error)}`,
          );
        }
      }

      setCurrentUploadStep('metadata');
      toast.info(
        'Preparing your Release NFT upload. Your wallet may request a signature if the selected upload provider requires one.',
        { duration: 8000 },
      );

      if (!iconFile) {
        throw new Error(
          'App icon could not be prepared for upload. Please try again.',
        );
      }

      const publisherContactEmail =
        dappData.contactEmail ||
        dappData.supportEmail ||
        dappData.publisherEmail;
      const supportEmail = dappData.supportEmail || publisherContactEmail || '';
      const { publisherName, publisherWebsite, licenseUrl, privacyPolicyUrl } =
        dappData;
      const releaseMetadata = getResolvedReleaseMetadata(releaseData, dappData);
      const {
        shortDescription,
        longDescription,
        newInVersion,
        localizedName,
        locales,
      } = releaseMetadata;

      const needsMetadataBackfill =
        shortDescription !== trimToUndefined(releaseData.shortDescription) ||
        longDescription !== trimToUndefined(releaseData.longDescription) ||
        localizedName !== trimToUndefined(releaseData.localizedName) ||
        newInVersion !== trimToUndefined(releaseData.newInVersion);

      if (needsMetadataBackfill) {
        try {
          await trpc.release.saveReleaseMetadata.mutate({
            releaseId: releaseData.id,
            newInVersion,
          });
          addBreadcrumb(
            'Backfilled missing release metadata before mint',
            'nft-mint',
            {
              releaseId: releaseData.id,
              usedDefaultReleaseNotes: !trimToUndefined(
                releaseData.newInVersion,
              ),
            },
          );
        } catch (metadataError) {
          addBreadcrumb('Release metadata backfill failed', 'nft-mint', {
            releaseId: releaseData.id,
            reason:
              metadataError instanceof Error
                ? metadataError.message
                : 'Unknown error',
          });
        }
      }

      const missingMetadataFields: string[] = [];
      if (!publisherName) missingMetadataFields.push('publisher name');
      if (!publisherWebsite) missingMetadataFields.push('publisher website');
      if (!publisherContactEmail) missingMetadataFields.push('contact email');
      if (!licenseUrl) missingMetadataFields.push('license URL');
      if (!privacyPolicyUrl) missingMetadataFields.push('privacy policy URL');
      if (!shortDescription) missingMetadataFields.push('short description');
      if (!longDescription) missingMetadataFields.push('long description');
      if (!localizedName) missingMetadataFields.push('localized app name');
      if (!newInVersion) missingMetadataFields.push("what's new summary");

      if (missingMetadataFields.length > 0) {
        throw new Error(
          `Missing required release metadata: ${missingMetadataFields.join(', ')}`,
        );
      }

      if (
        !publisherName ||
        !publisherWebsite ||
        !publisherContactEmail ||
        !licenseUrl ||
        !privacyPolicyUrl ||
        !shortDescription ||
        !longDescription ||
        !newInVersion
      ) {
        throw new Error('Required metadata validation failed');
      }

      const uploadResult = await uploadReleaseNftData(
        wallet,
        {
          releaseName: releaseNftName,
          releaseId: releaseData.id,
          releaseVersion: releaseData.releaseVersion,
          androidPackage: releaseData.androidPackage,
          versionCode: releaseData.versionCode,
          permissions: releaseData.permissions,
          minSdk: releaseData.minSdk,
          targetSdk: releaseData.targetSdk,
          certificateFingerprint: releaseData.certificateFingerprint,
          publisherAddress: releaseData.publisherAddress,
          apkFile,
          existingApk: existingApk ?? undefined,
          apkFileHash: releaseData.releaseFileHash,
          iconFile,
          screenshotFiles:
            screenshotFiles.length > 0 ? screenshotFiles : undefined,
          bannerFile,
          featureGraphicFile,
          publisherName,
          publisherWebsite,
          publisherEmail: publisherContactEmail,
          publisherSupportEmail: supportEmail,
          licenseUrl,
          copyrightUrl: '',
          privacyPolicyUrl,
          shortDescription,
          longDescription,
          whatsNew: newInVersion,
          localizedName: localizedName || dappData.dappName,
          locales,
        },
        trpc,
        (progress, message, isSignatureInProgress) => {
          setUploadProgress(progress);
          setUploadStatusMessage(message || null);
          setIsWaitingForSignature(!!isSignatureInProgress);
        },
      );

      setCurrentUploadStep('complete');
      setUploadProgress(100);
      setUploadStatusMessage('Upload complete!');
      setIsWaitingForSignature(false);

      const uploadResultTyped = uploadResult as UploadResult;
      const result = await mintReleaseNftWorkflow({
        wallet,
        trpc,
        release: {
          ...release,
          releaseMetadataUri: uploadResult.metadataUri,
        },
        uploadProvider: uploadResultTyped.uploadProvider,
        uploadProviderId: uploadResultTyped.uploadProviderId,
      });

      completeMint(result);
    } catch (error) {
      handleMintError(error, {
        getCaptureContext: ({
          errorMessage,
          nextRetryCount,
          walletAddress,
        }) => ({
          operation: 'Release NFT mint failed',
          component: 'ReleaseNftMintModal',
          nft_type: 'release',
          stage: 'frontend_mint',
          releaseId: releaseData.id,
          releaseName: releaseNftName,
          releaseVersion: releaseData.releaseVersion,
          androidPackage: releaseData.androidPackage,
          versionCode: releaseData.versionCode,
          publisherAddress: releaseData.publisherAddress,
          appMintAddress: releaseData.appMintAddress,
          dappName: dappData.dappName,
          walletConnected: !!publicKey,
          walletAddress,
          uploadStep: currentUploadStep,
          retryCount: nextRetryCount,
          apkDownloadTimeoutMs,
          releaseFileSize: releaseData.releaseFileSize,
          errorMessage,
        }),
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <ReleaseNftMintModalView
      releaseData={releaseData}
      dappData={dappData}
      isLoading={isLoading}
      mintResult={mintResult}
      lastError={lastError}
      retryCount={retryCount}
      connected={connected}
      connectedWalletAddress={publicKey?.toBase58() ?? null}
      isCorrectWallet={isCorrectWallet}
      uploadStep={uploadStep}
      uploadProgress={uploadProgress}
      uploadStepDisplay={getUploadStepDisplay()}
      onMint={handleMint}
      onClose={onClose}
    />
  );
}

interface ReleaseNftMintModalViewProps {
  releaseData: ReleaseNftMintModalProps['releaseData'];
  dappData: ReleaseNftMintModalProps['dappData'];
  isLoading: boolean;
  mintResult: { mintAddress: string; txSignature: string } | null;
  lastError: string | null;
  retryCount: number;
  connected: boolean;
  connectedWalletAddress: string | null;
  isCorrectWallet: boolean;
  uploadStep: UploadStep;
  uploadProgress: number;
  uploadStepDisplay: string;
  onMint: () => void;
  onClose: () => void;
  walletButton?: ReactNode;
}

export function ReleaseNftMintModalView({
  releaseData,
  dappData,
  isLoading,
  mintResult,
  lastError,
  retryCount,
  connected,
  connectedWalletAddress,
  isCorrectWallet,
  uploadStep,
  uploadProgress,
  uploadStepDisplay,
  onMint,
  onClose,
  walletButton,
}: ReleaseNftMintModalViewProps) {
  return (
    <NftMintModalFrame
      title="Mint Release NFT"
      description={
        <>
          Create a Release NFT to publish version {releaseData.releaseVersion}{' '}
          on-chain.
        </>
      }
      isLoading={isLoading}
      onClose={onClose}
    >
      {mintResult ? (
        <NftMintSuccessBlock
          title="Release NFT Minted Successfully!"
          description={
            <>
              Version {releaseData.releaseVersion} has been published on the
              Solana blockchain.
            </>
          }
          mintAddress={mintResult.mintAddress}
          txSignature={mintResult.txSignature}
        />
      ) : (
        <div className="space-y-4">
          <div className="p-4 bg-neutral-800 rounded-xl border border-neutral-700">
            <div className="text-sm font-medium text-neutral-300 mb-3">
              Release NFT Details
            </div>

            <div className="space-y-2">
              <div className="flex justify-between text-xs">
                <span className="text-neutral-400">Name:</span>
                <span className="text-white">
                  {dappData.dappName} v{releaseData.releaseVersion}
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-neutral-400">Package:</span>
                <span className="text-white font-mono">
                  {releaseData.androidPackage}
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-neutral-400">Version Code:</span>
                <span className="text-white">{releaseData.versionCode}</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-neutral-400">APK Size:</span>
                <span className="text-white">
                  {(releaseData.releaseFileSize / (1024 * 1024)).toFixed(2)} MB
                </span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-neutral-400">Type:</span>
                <span className="text-white">Release (Immutable)</span>
              </div>
              <div className="flex justify-between text-xs">
                <span className="text-neutral-400">Network:</span>
                <span className="text-white">
                  Solana {SOLANA_NETWORK_LABEL}
                </span>
              </div>
            </div>
          </div>

          {isLoading && uploadStep !== 'preparing' && (
            <div className="p-4 bg-neutral-800 rounded-xl border border-neutral-700">
              <div className="flex items-center gap-3 mb-3">
                <CloudUploadIcon className="size-5 text-blue-500 animate-pulse" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-white">
                    {uploadStepDisplay}
                  </p>
                </div>
                <span className="text-sm text-neutral-400">
                  {uploadProgress}%
                </span>
              </div>
              <div className="w-full bg-neutral-700 rounded-full h-2 overflow-hidden">
                <div
                  className="bg-gradient-to-r from-blue-500 to-teal-500 h-full rounded-full transition-all duration-300 ease-out"
                  style={{ width: `${uploadProgress}%` }}
                />
              </div>
              {uploadStep === 'apk' && (
                <p className="text-xs text-neutral-400 mt-2">
                  This may take a while for large APK files…
                </p>
              )}
            </div>
          )}

          <NftMintRequiredWalletBlock
            walletAddress={releaseData.publisherAddress}
          />

          {connected && connectedWalletAddress && (
            <NftMintConnectedWalletBlock
              walletAddress={connectedWalletAddress}
              isCorrectWallet={isCorrectWallet}
              wrongWalletMessage="Please connect the app wallet to mint the Release NFT"
              walletButton={walletButton}
            />
          )}

          <div className="flex items-start gap-3 p-4 bg-blue-900/20 rounded-xl border border-blue-700/30">
            <InfoIcon className="size-4 fill-white flex-shrink-0 mt-0.5" />
            <div className="text-xs text-blue-300">
              <p>
                This will create an immutable Release NFT on Solana blockchain.
                You'll need to:
              </p>
              <ol className="mt-1 space-y-1 list-decimal list-inside">
                <li>
                  Authorize the configured upload provider if your wallet
                  prompts for it
                </li>
                <li>Upload the APK and assets to permanent storage</li>
                <li>Approve the NFT minting transaction</li>
                <li>
                  Pay network fees and rent (≈0.05-0.1 SOL depending on APK
                  size)
                </li>
              </ol>
            </div>
          </div>

          {lastError && (
            <NftMintErrorBlock error={lastError} retryCount={retryCount} />
          )}

          <div className="flex flex-col gap-2">
            <Button
              onClick={onMint}
              disabled={!connected || !isCorrectWallet || isLoading}
              submitting={isLoading}
              className="w-full"
            >
              {isLoading
                ? 'Processing...'
                : lastError && retryCount > 0
                  ? 'Retry'
                  : 'Mint Release NFT'}
            </Button>

            {!connected && (
              <NftMintDisconnectedWalletPrompt
                message="Please connect the app wallet to mint the Release NFT"
                walletButton={walletButton}
              />
            )}

            {connected && !isCorrectWallet && <NftMintWrongWalletMessage />}

            <Button
              variant="secondary"
              onClick={onClose}
              disabled={isLoading}
              className="w-full"
            >
              Cancel
            </Button>
          </div>
        </div>
      )}
    </NftMintModalFrame>
  );
}
